import { fetchNetworkExplorerStats, type NetworkExplorerStats } from '@/api/networkExplorerApi';
import type { StatsPeriod } from '@/api/statsApi';
import { useStatsFilters } from '@/stats/StatsFiltersContext';
import { useCachedStats } from './useCachedStats';

/** Exported for test isolation only — do not call in production code. */
export function _clearNetworkExplorerStatsCache() {
    cache.clear();
}

// One estate, one figure set per (period, committed filters): a module-level cache kept
// across remounts within the browser session (like the single-client stats cache) so leaving
// and re-entering the explorer, or switching the period back, does not flash a loading
// skeleton.
const cache = new Map<string, NetworkExplorerStats>();

/**
 * The estate-wide statistics for the Network Explorer header over the chosen period, under
 * the explorer's committed map filters (bridged up from MapView through the stats filters
 * context, exactly as the Find & Fix bar's are). Takes no client — the row set is the
 * server's to decide from the caller's permission.
 */
export function useNetworkExplorerStats(period: StatsPeriod): {
    data: NetworkExplorerStats | null;
    isLoading: boolean;
    isError: boolean;
} {
    const { filters, filtersKey } = useStatsFilters();

    return useCachedStats(
        {
            key: `${period}:${filtersKey}`,
            fetch: (signal) => fetchNetworkExplorerStats(period, filters, signal),
        },
        cache,
    );
}
